/**
 * Comptabilite — Vue liste des dossiers comptables mensuels.
 * Parité BE-TAYE : dossier courant en évidence + historique + envoi au cabinet.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderArchive, Send, Download, Settings as SettingsIcon, RefreshCw, CheckCircle2, Clock, AlertCircle, ExternalLink, Sparkles, Plus, Zap, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  in_progress: { label: "En cours", icon: Clock, color: "bg-blue-50 text-blue-700 border-blue-200" },
  ready: { label: "Prêt", icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sent: { label: "Envoyé", icon: Send, color: "bg-violet-50 text-violet-700 border-violet-200" },
  downloaded: { label: "Téléchargé", icon: CheckCircle2, color: "bg-teal-50 text-teal-700 border-teal-200" },
  expired: { label: "Expiré", icon: AlertCircle, color: "bg-slate-100 text-slate-600" },
};

export default function Comptabilite() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("accounting.write") || hasPermission("admin");
  const [current, setCurrent] = useState(null);
  const [folders, setFolders] = useState([]);
  const [busy, setBusy] = useState(null);
  const [insights, setInsights] = useState([]);
  const [newPeriod, setNewPeriod] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [c, f, ins] = await Promise.all([
        api.get("/accounting/folders/current"),
        api.get("/accounting/folders"),
        api.get("/accounting/insights"),
      ]);
      setCurrent(c.data);
      setFolders((f.data.folders || []).filter(x => !x.is_current));
      setInsights(ins.data.insights || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const syncFolder = async (fid) => {
    setBusy(fid);
    try {
      const r = await api.post(`/accounting/folders/${fid}/sync`);
      toast.success(`${r.data.total} document(s) ajouté(s)`); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  const sendFolder = async (fid) => {
    if (!window.confirm("Envoyer ce dossier à l'expert-comptable ?")) return;
    setBusy(fid);
    try {
      const r = await api.post(`/accounting/folders/${fid}/send`, {});
      toast.success(`Envoyé à ${r.data.sent_to}`); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  if (!current) return <div className="p-6">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <FolderArchive className="w-6 h-6 text-blue-600" />Comptabilité
          </h1>
          <p className="text-sm text-slate-500">Transmission mensuelle du dossier à votre expert-comptable</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/app/accounting/settings")} data-testid="ct-settings-btn">
          <SettingsIcon className="w-4 h-4 mr-1.5" />Paramètres
        </Button>
      </div>

      {/* Barre d'actions intelligentes */}
      <div className="flex flex-wrap gap-2 items-center bg-slate-50 rounded-lg p-3">
        <Button variant="outline" size="sm" onClick={async () => {
          if (!window.confirm("Scanner tout l'historique et créer/synchroniser les dossiers manquants ?")) return;
          setBusy("backfill");
          try { const r = await api.post("/accounting/backfill");
            toast.success(`${r.data.folders_created} dossier(s) créé(s), ${r.data.total_added} document(s) ajouté(s)`); load(); }
          catch (e) { toast.error(apiError(e)); }
          finally { setBusy(null); }
        }} disabled={busy === "backfill"} data-testid="ct-backfill-btn">
          <Zap className={`w-4 h-4 mr-1.5 ${busy === "backfill" ? "animate-pulse" : ""}`} />Rattrapage historique
        </Button>
        <Button variant="outline" size="sm" onClick={async () => {
          if (!window.confirm("Rattraper toutes les factures ventes + achats qui ne seraient pas déjà dans un dossier compta ? L'opération est idempotente.")) return;
          setBusy("resync");
          const t = toast.loading("Analyse des factures en cours…");
          try {
            const r = await api.post("/accounting/resync-all-missing");
            toast.dismiss(t);
            toast.success(
              <div>
                <p className="font-medium">Rattrapage terminé</p>
                <p className="text-xs">
                  {r.data.sales_added_or_updated} vente(s) · {r.data.purchases_added_or_updated} achat(s)
                  {r.data.total === 0 ? " · rien à ajouter" : ""}
                </p>
              </div>,
              { duration: 6000 }
            );
            load();
          } catch (e) {
            toast.dismiss(t);
            toast.error(apiError(e));
          } finally { setBusy(null); }
        }} disabled={busy === "resync" || !canWrite} data-testid="ct-resync-missing-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${busy === "resync" ? "animate-spin" : ""}`} />
          Rattraper factures manquantes
        </Button>
        <div className="flex items-center gap-1.5">
          <Input type="month" value={newPeriod} onChange={(e) => setNewPeriod(e.target.value)}
            className="w-40 h-9" data-testid="ct-new-period" />
          <Button size="sm" onClick={async () => {
            if (!newPeriod) { toast.error("Mois requis"); return; }
            setCreating(true);
            try {
              const r = await api.post("/accounting/folders", { period: newPeriod });
              toast.success(`Dossier ${newPeriod} créé/récupéré`);
              navigate(`/app/accounting/folders/${r.data.id}`);
            } catch (e) { toast.error(apiError(e)); }
            finally { setCreating(false); }
          }} disabled={creating} data-testid="ct-create-period-btn">
            <Plus className="w-4 h-4 mr-1" />Ouvrir mois
          </Button>
        </div>
      </div>

      {/* Insights intelligents */}
      {insights.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="ct-insights-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />Anomalies détectées ({insights.length})
          </CardTitle></CardHeader>
          <CardContent className="space-y-1.5 pt-0">
            {insights.map((ins, i) => (
              <div key={i} className={`flex items-start gap-2 text-sm p-2 rounded ${
                ins.level === "critical" ? "bg-red-50 text-red-800" :
                ins.level === "warning" ? "bg-amber-50 text-amber-800" : "bg-blue-50 text-blue-800"
              }`} data-testid={`insight-${i}`}>
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="flex-1">{ins.message}</span>
                {ins.period && (
                  <button className="text-xs underline" onClick={async () => {
                    try { const r = await api.post("/accounting/folders", { period: ins.period });
                      navigate(`/app/accounting/folders/${r.data.id}`); }
                    catch (e) { toast.error(apiError(e)); }
                  }}>Ouvrir</button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dossier courant en évidence */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50/40 to-transparent" data-testid="current-folder-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Dossier en cours</p>
              <CardTitle className="text-2xl mt-1">{current.display_name}</CardTitle>
            </div>
            <Badge variant="outline" className={STATUS[current.status]?.color || ""}>
              {STATUS[current.status]?.label || current.status}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Complétude</p>
                <div className="flex items-center gap-2 mt-1">
                  <Progress value={current.completeness.score} className="h-2 flex-1" />
                  <span className="text-lg font-bold font-mono">{current.completeness.score}%</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Documents</p>
                <p className="text-2xl font-bold font-mono">{current.completeness.files_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Manquant</p>
                <p className="text-sm text-red-600 font-medium">
                  {current.completeness.missing.length === 0 ? "—" : current.completeness.missing.join(", ")}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/accounting/folders/${current.id}`)} data-testid="open-current-btn">
                Ouvrir <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
              {canWrite && (
                <>
                  <Button variant="outline" size="sm" onClick={() => syncFolder(current.id)} disabled={busy === current.id} data-testid="sync-current-btn">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy === current.id ? "animate-spin" : ""}`} />Synchroniser
                  </Button>
                  <Button size="sm" onClick={() => sendFolder(current.id)} disabled={busy === current.id} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-current-btn">
                    <Send className="w-3.5 h-3.5 mr-1" />Envoyer au cabinet
                  </Button>
                </>
              )}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/accounting/folders/${current.id}/download`} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm">
                  <Download className="w-3.5 h-3.5 mr-1" />ZIP
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Historique */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Historique</h2>
        <Card><CardContent className="p-0">
          {folders.length === 0 ? (
            <p className="p-8 text-sm text-slate-500 text-center">Aucun dossier antérieur.</p>
          ) : (
            <div className="divide-y">
              {folders.map((f, i) => {
                const S = STATUS[f.status] || STATUS.in_progress;
                return (
                  <div key={f.id} className="p-4 flex items-center gap-3 hover:bg-slate-50" data-testid={`folder-row-${i}`}>
                    <FolderArchive className="w-5 h-5 text-slate-400" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{f.display_name}</div>
                      <div className="text-xs text-slate-500">{f.completeness.files_count} fichiers · {f.completeness.score}% complétude {f.sent_to ? `· envoyé à ${f.sent_to}` : ""}</div>
                    </div>
                    <Badge variant="outline" className={S.color}>{S.label}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/app/accounting/folders/${f.id}`)}>
                      Ouvrir <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                    {canWrite && !["sent", "downloaded"].includes(f.status) && (
                      <Button variant="ghost" size="sm" onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Supprimer le dossier ${f.display_name} ?`)) return;
                        try { await api.delete(`/accounting/folders/${f.id}`);
                          toast.success("Dossier supprimé"); load(); }
                        catch (err) { toast.error(apiError(err)); }
                      }} className="text-red-600 h-7" data-testid={`del-folder-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}
